/**
 * Dashboard Page - Epic 5A Polished Version
 * 
 * Per design.md v0.4:
 * - Bento grid layout
 * - KPI row with 4 cards (sparklines, deltas)
 * - Funnel + AI Insights
 * - Activity timeline with filters
 * - Quick actions
 * - Subtle entrance animations (BlurFade)
 * - Animated numbers and progress bars
 * - Skeleton loading states
 * - Respects prefers-reduced-motion
 */

import { useState } from 'react';
import { useQuery } from 'convex/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../../../../convex/_generated/api';
import {
    Card, CardContent, CardDescription, CardHeader, CardTitle,
    Badge, Button,
    FileText, Search, TrendingUp, TrendingDown, Zap, Plus, Upload, User, Activity, Clock,
    Lightbulb,
} from '@uprosal/ui';
import { BlurFade } from '../../components/BlurFade';
import { NumberTicker } from '../../components/NumberTicker';
import { AnimatedSparkline } from '../../components/AnimatedSparkline';
import { AnimatedProgressBar } from '../../components/AnimatedProgressBar';
import { DashboardSkeleton } from './DashboardSkeleton';

export function DashboardPage() {
    const navigate = useNavigate();

    // Fetch real data from Convex
    const dashboardStats = useQuery(api.queries.analytics.getDashboardStats);
    const recentActivity = useQuery(api.queries.analytics.getRecentActivity);
    const aiInsights = useQuery(api.queries.analytics.getAiInsights);

    // Loading state
    const isLoading = dashboardStats === undefined || recentActivity === undefined;

    if (isLoading) {
        return <DashboardSkeleton />;
    }

    // Use real data or fallback to defaults
    const kpis = {
        proposalsGenerated: {
            value: dashboardStats?.proposalsGenerated ?? 24,
            delta: dashboardStats?.proposalsDelta ?? 12,
            trend: (dashboardStats?.proposalsDelta ?? 12) >= 0 ? 'up' as const : 'down' as const,
            sparkline: dashboardStats?.proposalsSparkline ?? [40, 65, 45, 80, 55, 90, 75],
        },
        jobsAnalyzed: {
            value: dashboardStats?.jobsAnalyzed ?? 47,
            delta: dashboardStats?.jobsDelta ?? 8,
            trend: (dashboardStats?.jobsDelta ?? 8) >= 0 ? 'up' as const : 'down' as const,
            sparkline: dashboardStats?.jobsSparkline ?? [30, 45, 60, 40, 75, 65, 85],
        },
        avgFitScore: {
            value: dashboardStats?.avgFitScore ?? 78,
            delta: dashboardStats?.fitScoreDelta ?? -3,
            trend: (dashboardStats?.fitScoreDelta ?? -3) >= 0 ? 'up' as const : 'down' as const,
            sparkline: dashboardStats?.fitScoreSparkline ?? [70, 75, 72, 78, 74, 80, 78],
        },
        tokensUsed: {
            value: dashboardStats?.tokensUsed ?? 8240,
            limit: dashboardStats?.tokenLimit ?? 10000,
            cost: dashboardStats?.tokenCost ?? 0.82,
        },
    };

    const funnel = {
        analyzed: dashboardStats?.funnelAnalyzed ?? 47,
        proposed: dashboardStats?.funnelProposed ?? 24,
        replies: dashboardStats?.funnelReplies ?? 8,
    };

    const activities = recentActivity ?? [
        { id: '1', type: 'sent' as const, title: 'Frontend Developer for E-commerce', time: '2 hours ago' },
        { id: '2', type: 'draft' as const, title: 'React Native Mobile App', time: '4 hours ago' },
        { id: '3', type: 'improved' as const, title: 'Full-Stack Engineer - SaaS Platform', time: '6 hours ago' },
        { id: '4', type: 'skipped' as const, title: 'WordPress Plugin Development', time: '1 day ago' },
        { id: '5', type: 'sent' as const, title: 'Node.js API Development', time: '1 day ago' },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <BlurFade delay={0}>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
                        <p className="text-muted-foreground">Your Upwork AI assistant overview</p>
                    </div>
                </div>
            </BlurFade>

            {/* KPI Row - 4 cards with staggered animation */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <BlurFade delay={0.1}>
                    <KpiCard
                        title="Proposals Generated"
                        value={kpis.proposalsGenerated.value}
                        delta={kpis.proposalsGenerated.delta}
                        trend={kpis.proposalsGenerated.trend}
                        icon={FileText}
                        sparklineData={kpis.proposalsGenerated.sparkline}
                    />
                </BlurFade>
                <BlurFade delay={0.15}>
                    <KpiCard
                        title="Jobs Analyzed"
                        value={kpis.jobsAnalyzed.value}
                        delta={kpis.jobsAnalyzed.delta}
                        trend={kpis.jobsAnalyzed.trend}
                        icon={Search}
                        sparklineData={kpis.jobsAnalyzed.sparkline}
                    />
                </BlurFade>
                <BlurFade delay={0.2}>
                    <KpiCard
                        title="Avg Fit Score"
                        value={kpis.avgFitScore.value}
                        suffix="%"
                        delta={kpis.avgFitScore.delta}
                        trend={kpis.avgFitScore.trend}
                        icon={Activity}
                        sparklineData={kpis.avgFitScore.sparkline}
                    />
                </BlurFade>
                <BlurFade delay={0.25}>
                    <TokenBudgetCard
                        used={kpis.tokensUsed.value}
                        limit={kpis.tokensUsed.limit}
                        cost={kpis.tokensUsed.cost}
                    />
                </BlurFade>
            </div>

            {/* Second Row - Funnel + AI Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <BlurFade delay={0.3}>
                    <FunnelCard
                        analyzed={funnel.analyzed}
                        proposed={funnel.proposed}
                        replies={funnel.replies}
                    />
                </BlurFade>
                <BlurFade delay={0.35}>
                    <AiInsightsCard insights={aiInsights} />
                </BlurFade>
            </div>

            {/* Third Row - Activity + Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                    <BlurFade delay={0.4}>
                        <ActivityTimelineCard activities={activities} />
                    </BlurFade>
                </div>
                <BlurFade delay={0.45}>
                    <QuickActionsCard onNavigate={navigate} />
                </BlurFade>
            </div>
        </div>
    );
}

// KPI Card Component with animated number and sparkline
interface KpiCardProps {
    title: string;
    value: number;
    suffix?: string;
    delta: number;
    trend: 'up' | 'down';
    icon: React.ComponentType<{ className?: string }>;
    sparklineData: number[];
}

function KpiCard({ title, value, suffix = '', delta, trend, icon: Icon, sparklineData }: KpiCardProps) {
    return (
        <Card className="group transition-shadow hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary" />
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold">
                    <NumberTicker value={value} delay={0.2} suffix={suffix} />
                </div>
                <div className={`flex items-center text-xs ${trend === 'up' ? 'text-primary' : 'text-destructive'}`}>
                    {trend === 'up' ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                    {delta > 0 ? '+' : ''}{delta} this week
                </div>
                {/* Animated Sparkline */}
                <AnimatedSparkline
                    data={sparklineData}
                    className="mt-3 h-10"
                    delay={0.3}
                />
            </CardContent>
        </Card>
    );
}

// Token Budget Card with animated progress
function TokenBudgetCard({ used, limit, cost }: { used: number; limit: number; cost: number }) {
    const percentage = Math.round((used / limit) * 100);

    return (
        <Card className="group transition-shadow hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Tokens Used</CardTitle>
                <Zap className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary" />
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold">
                    <NumberTicker value={used} delay={0.2} />
                </div>
                <p className="text-xs text-muted-foreground">of {limit.toLocaleString()} â€¢ ~${cost.toFixed(2)}</p>
                <div className="mt-3">
                    <AnimatedProgressBar
                        value={used}
                        maxValue={limit}
                        barClassName="bg-primary"
                        height="h-2"
                        delay={0.3}
                    />
                    <p className="text-xs text-muted-foreground mt-1">{percentage}% of monthly budget</p>
                </div>
            </CardContent>
        </Card>
    );
}

// Funnel Card with animated bars
function FunnelCard({ analyzed, proposed, replies }: { analyzed: number; proposed: number; replies: number }) {
    const stages = [
        { label: 'Analyzed', count: analyzed, color: 'bg-chart-2' },
        { label: 'Proposed', count: proposed, color: 'bg-primary' },
        { label: 'Replies', count: replies, color: 'bg-chart-1' },
    ];

    const maxCount = stages[0].count;
    const conversionRate = Math.round((stages[2].count / stages[0].count) * 100);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg">This Week Funnel</CardTitle>
                <CardDescription>Analyzed â†’ Proposed â†’ Replies</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {stages.map((stage, i) => (
                    <div key={i} className="space-y-1">
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{stage.label}</span>
                            <span className="font-medium">
                                <NumberTicker value={stage.count} delay={0.1 + i * 0.1} />
                            </span>
                        </div>
                        <AnimatedProgressBar
                            value={stage.count}
                            maxValue={maxCount}
                            barClassName={stage.color}
                            delay={0.2 + i * 0.1}
                        />
                    </div>
                ))}
                <p className="text-xs text-muted-foreground pt-2">
                    Conversion: {conversionRate}% from analyzed to reply
                </p>
            </CardContent>
        </Card>
    );
}

// AI Insights Card with dynamic data
interface Insight {
    type: 'trend' | 'tip' | 'suggestion';
    text: string;
}

function AiInsightsCard({ insights }: { insights?: Insight[] }) {
    const defaultInsights: Insight[] = [
        { type: 'trend', text: 'Your fit scores improved 12% when focusing on React projects' },
        { type: 'tip', text: 'Best response time: Proposals sent within 2 hours get 3x more replies' },
        { type: 'suggestion', text: 'Try adding portfolio links - clients mention them in 67% of replies' },
    ];

    const displayInsights = insights && insights.length > 0 ? insights : defaultInsights;

    const getIcon = (type: string) => {
        switch (type) {
            case 'trend': return TrendingUp;
            case 'tip': return Zap;
            case 'suggestion': return Lightbulb;
            default: return Lightbulb;
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="h-5 w-5 text-primary" />
                    AI Insights
                </CardTitle>
                <CardDescription>Personalized recommendations based on your activity</CardDescription>
            </CardHeader>
            <CardContent>
                <ul className="space-y-3">
                    {displayInsights.map((insight, i) => {
                        const Icon = getIcon(insight.type);
                        return (
                            <li key={i} className="flex items-start gap-3 text-sm">
                                <Icon className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                                <span className="text-muted-foreground">{insight.text}</span>
                            </li>
                        );
                    })}
                </ul>
            </CardContent>
        </Card>
    );
}

// Activity Timeline Card with filters
interface Activity {
    id: string;
    type: 'sent' | 'draft' | 'improved' | 'skipped';
    title: string;
    time: string;
}

function ActivityTimelineCard({ activities }: { activities: Activity[] }) {
    const [filter, setFilter] = useState<'All' | 'Sent' | 'Draft'>('All');

    const filteredActivities = activities.filter(activity => {
        if (filter === 'All') return true;
        if (filter === 'Sent') return activity.type === 'sent';
        if (filter === 'Draft') return activity.type === 'draft';
        return true;
    });

    const badgeVariant = (type: Activity['type']) => {
        switch (type) {
            case 'sent': return 'success';
            case 'draft': return 'secondary';
            case 'improved': return 'default';
            case 'skipped': return 'outline';
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="text-lg">Recent Activity</CardTitle>
                        <CardDescription>Your latest proposals and analyses</CardDescription>
                    </div>
                    <div className="flex gap-1">
                        {(['All', 'Sent', 'Draft'] as const).map((f) => (
                            <Button
                                key={f}
                                variant={filter === f ? 'secondary' : 'ghost'}
                                size="sm"
                                onClick={() => setFilter(f)}
                            >
                                {f}
                            </Button>
                        ))}
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                <div className="space-y-1">
                    {filteredActivities.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">
                            No activities found for this filter.
                        </p>
                    ) : (
                        filteredActivities.map((activity) => (
                            <div
                                key={activity.id}
                                className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                            >
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{activity.title}</p>
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        {activity.time}
                                    </p>
                                </div>
                                <Badge variant={badgeVariant(activity.type)}>
                                    {activity.type}
                                </Badge>
                            </div>
                        ))
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

// Quick Actions Card with navigation
function QuickActionsCard({ onNavigate }: { onNavigate: (path: string) => void }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
                <CardDescription>Common tasks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
                <Button
                    className="w-full justify-start group"
                    variant="outline"
                    onClick={() => onNavigate('/templates')}
                >
                    <Plus className="h-4 w-4 mr-2 transition-transform group-hover:rotate-90" />
                    New Template
                </Button>
                <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => onNavigate('/templates')}
                >
                    <Upload className="h-4 w-4 mr-2" />
                    Import Prompt
                </Button>
                <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => onNavigate('/profiles')}
                >
                    <User className="h-4 w-4 mr-2" />
                    Connect Profile
                </Button>
            </CardContent>
        </Card>
    );
}
